/**
 * Sysadmin Catalog → Workflow Detail (PR-K1).
 *
 * 6 tabs: Overview · Definition · Composition · Versions · Clones · Audit.
 *
 * Lifecycle action bar at the top: Publish / Deprecate / Retire /
 * Restore-to-draft / Delete. Publish is gated by the API's
 * dangling-reference validator — the UI surfaces 422 details with the
 * offender list.
 *
 * Composition pickers reach across to system_skills / system_tools /
 * argus_wrapper_functions via existing sysadmin hooks.
 *
 * Mirrors catalog.skills.$id.tsx + catalog.tools.$id.tsx patterns.
 * Definition tab uses a JSON editor (Monaco-style textarea) for v1; a
 * visual graph editor is deferred to a follow-up.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Workflow,
  ArrowLeft,
  AlertTriangle,
  ShieldCheck,
  Pencil,
  Save,
  Eye,
  Trash2,
  X,
  Loader2,
  BookText,
  Wrench,
  FunctionSquare,
  Sparkles,
  History,
  GitFork,
  ScrollText,
  Plus,
  MinusCircle,
  Search,
  Code2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import {
  useCatalogWorkflow,
  useUpdateCatalogWorkflow,
  useDeleteCatalogWorkflow,
  usePublishCatalogWorkflow,
  useUnpublishCatalogWorkflow,
  useCatalogWorkflowClones,
  type CatalogWorkflowDetail,
  type WorkflowStatus,
  type CloneSyncStatus,
} from '@/lib/use-sysadmin-catalog-workflows';
import {
  useSysAdminSystemTools,
  useSysAdminSystemSkills,
} from '@/lib/use-sysadmin-system-catalog';
import { useWrapperFunctions } from '@/lib/use-argus-models';

export const Route = createFileRoute('/sysadmin/catalog/workflows/$id')({
  component: SysadminCatalogWorkflowDetailPage,
});

function statusPillClass(status: WorkflowStatus): string {
  switch (status) {
    case 'published':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    case 'draft':
      return 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30';
    case 'deprecated':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
    case 'retired':
      return 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-400 border-zinc-500/30';
  }
}

function syncStatusPillClass(status: CloneSyncStatus): string {
  switch (status) {
    case 'forked':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    case 'synced':
      return 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/30';
    case 'diverged':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
    case 'out_of_date':
      return 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30';
  }
}

function SysadminCatalogWorkflowDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useCatalogWorkflow(id);
  const workflow = data?.data;

  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteCatalogWorkflow();

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
            Loading workflow…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{error?.message ?? 'Workflow not found.'}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDelete = async () => {
    try {
      await del.mutateAsync({ id });
      toast.success(`Deleted "${workflow.displayName}"`);
      navigate({ to: '/sysadmin/catalog/workflows' });
    } catch (err) {
      toast.error('Delete failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Header + back */}
      <div className="flex items-start gap-3">
        <Link
          to="/sysadmin/catalog/workflows"
          className="text-muted-foreground hover:text-foreground p-1.5 rounded -ml-1.5"
          title="Back to workflow templates"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <Workflow className="h-7 w-7 text-muted-foreground mt-0.5" />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight truncate">
            {workflow.displayName}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="font-mono text-[11px]">
              {workflow.slug}
            </Badge>
            <Badge
              variant="outline"
              className={cn(
                'text-[10px] capitalize border',
                statusPillClass(workflow.status),
              )}
            >
              {workflow.status}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {workflow.category}
            </Badge>
            <span className="text-xs text-muted-foreground tabular-nums">
              v{workflow.version}
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {workflow.cloneCount} clone{workflow.cloneCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </div>

      <LifecycleActionBar
        workflow={workflow}
        onDelete={() => setConfirmDelete(true)}
      />

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">
            <BookText className="h-3.5 w-3.5 mr-1.5" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="definition">
            <Code2 className="h-3.5 w-3.5 mr-1.5" />
            Definition
          </TabsTrigger>
          <TabsTrigger value="composition">
            <Wrench className="h-3.5 w-3.5 mr-1.5" />
            Composition
          </TabsTrigger>
          <TabsTrigger value="versions">
            <History className="h-3.5 w-3.5 mr-1.5" />
            Versions
          </TabsTrigger>
          <TabsTrigger value="clones">
            <GitFork className="h-3.5 w-3.5 mr-1.5" />
            Clones
          </TabsTrigger>
          <TabsTrigger value="audit">
            <ScrollText className="h-3.5 w-3.5 mr-1.5" />
            Audit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab workflow={workflow} />
        </TabsContent>

        <TabsContent value="definition">
          <DefinitionTab workflow={workflow} />
        </TabsContent>

        <TabsContent value="composition">
          <CompositionTab workflow={workflow} />
        </TabsContent>

        <TabsContent value="versions">
          <VersionsTab workflow={workflow} />
        </TabsContent>

        <TabsContent value="clones">
          <ClonesTab workflow={workflow} />
        </TabsContent>

        <TabsContent value="audit">
          <AuditTab workflow={workflow} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete workflow</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently soft-delete{' '}
              <code className="bg-muted px-1 rounded">{workflow.slug}</code>?
              Existing tenant clones survive — they're independent of the
              source row. Allowed only for draft or retired workflows.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete workflow
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Lifecycle action bar ─────────────────────────────────────────────────

function LifecycleActionBar({
  workflow,
  onDelete,
}: {
  workflow: CatalogWorkflowDetail;
  onDelete: () => void;
}) {
  const publish = usePublishCatalogWorkflow();
  const unpublish = useUnpublishCatalogWorkflow();
  const [unpublishDialog, setUnpublishDialog] = useState<{
    nextStatus: 'draft' | 'deprecated' | 'retired';
  } | null>(null);
  const [unpublishReason, setUnpublishReason] = useState('');

  const handlePublish = async () => {
    try {
      await publish.mutateAsync({ id: workflow.id });
      toast.success(`Published "${workflow.displayName}"`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Publish blocked', {
        description: msg,
      });
    }
  };

  const handleUnpublish = async () => {
    if (!unpublishDialog || !unpublishReason.trim()) return;
    try {
      await unpublish.mutateAsync({
        id: workflow.id,
        reason: unpublishReason.trim(),
        nextStatus: unpublishDialog.nextStatus,
      });
      toast.success(
        `Moved "${workflow.displayName}" to ${unpublishDialog.nextStatus}`,
      );
      setUnpublishDialog(null);
      setUnpublishReason('');
    } catch (err) {
      toast.error('Unpublish failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const canPublish =
    workflow.status === 'draft' || workflow.status === 'deprecated';
  const canDeprecate = workflow.status === 'published';
  const canRetire =
    workflow.status === 'published' || workflow.status === 'deprecated';
  const canRestoreDraft =
    (workflow.status === 'deprecated' || workflow.status === 'retired') &&
    workflow.cloneCount === 0;
  const canDelete =
    workflow.status === 'draft' || workflow.status === 'retired';

  return (
    <>
      <Card className="bg-muted/30">
        <CardContent className="py-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground mr-2">Lifecycle:</span>
          {canPublish && (
            <Button
              size="sm"
              onClick={handlePublish}
              disabled={publish.isPending}
              title="Publish — composition is validated against published skills/tools and active functions"
            >
              {publish.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
              )}
              Publish
            </Button>
          )}
          {/*
            Issue #746 — PR-U1 collapsed catalog lifecycle from a
            multi-state DAG (deprecated -> retired -> restore-to-draft)
            to a 2-state Q7 model (draft|published) with `enabled` for
            soft-disable and `deleted_at` for soft-delete. The backend
            POST /catalog-workflows/:id/unpublish endpoint now returns
            410 Gone, so the Deprecate / Retire / Restore-to-draft
            buttons were dead controls. Soft-disable lives on the
            details toggle; soft-delete is the existing Delete button.
            `canDeprecate` / `canRetire` / `canRestoreDraft` gates are
            kept for future re-introduction if the lifecycle is ever
            re-expanded.
          */}
          <div className="flex-1" />
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              className="text-destructive hover:bg-destructive/10"
              title="Soft-delete — only draft or retired"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete
            </Button>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={unpublishDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setUnpublishDialog(null);
            setUnpublishReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move to {unpublishDialog?.nextStatus}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Provide a reason for the audit log. This is required.
              {unpublishDialog?.nextStatus === 'draft' && (
                <>
                  <br />
                  <strong className="text-amber-600">
                    Note: Restoring to draft requires zero tenant clones
                    referencing this template.
                  </strong>
                </>
              )}
              {(unpublishDialog?.nextStatus === 'deprecated' ||
                unpublishDialog?.nextStatus === 'retired') &&
                workflow.cloneCount > 0 && (
                  <>
                    <br />
                    <span className="text-muted-foreground">
                      {workflow.cloneCount} tenant clone(s) will move to
                      `out_of_date` sync status. They remain functional
                      (clones are independent post-fork).
                    </span>
                  </>
                )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={unpublishReason}
            onChange={(e) => setUnpublishReason(e.target.value)}
            placeholder="Reason (e.g. replaced by workflow-v2, upstream API change, etc.)"
            rows={3}
            className="text-sm"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!unpublishReason.trim() || unpublish.isPending}
              onClick={handleUnpublish}
              className={cn(
                unpublishDialog?.nextStatus === 'retired' &&
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
              )}
            >
              {unpublish.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab({ workflow }: { workflow: CatalogWorkflowDetail }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    displayName: workflow.displayName,
    description: workflow.description,
    category: workflow.category,
  });
  const update = useUpdateCatalogWorkflow();

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        id: workflow.id,
        displayName: draft.displayName,
        description: draft.description,
        category: draft.category,
      });
      toast.success('Saved changes');
      setEditing(false);
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const isReadOnly = workflow.status === 'retired';
  const triggerMode =
    typeof workflow.triggerConfig.mode === 'string'
      ? (workflow.triggerConfig.mode as string)
      : 'conversational';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base">Identity</CardTitle>
            <CardDescription>
              Display metadata. Slug is immutable after creation.
            </CardDescription>
          </div>
          {!isReadOnly &&
            (editing ? (
              <div className="flex gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditing(false);
                    setDraft({
                      displayName: workflow.displayName,
                      description: workflow.description,
                      category: workflow.category,
                    });
                  }}
                  title="Discard changes"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={update.isPending}
                  title="Save changes (creates a new version)"
                >
                  {update.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Save
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(true)}
                title="Edit"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
            ))}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Slug</Label>
              <div className="font-mono text-xs mt-1">{workflow.slug}</div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              {editing ? (
                <Input
                  value={draft.category}
                  onChange={(e) =>
                    setDraft({ ...draft, category: e.target.value })
                  }
                  className="text-xs h-8 mt-1"
                />
              ) : (
                <div className="text-sm mt-1">{workflow.category}</div>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">
              Display name
            </Label>
            {editing ? (
              <Input
                value={draft.displayName}
                onChange={(e) =>
                  setDraft({ ...draft, displayName: e.target.value })
                }
                className="text-sm h-8 mt-1"
              />
            ) : (
              <div className="text-sm mt-1">{workflow.displayName}</div>
            )}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Description</Label>
            {editing ? (
              <Textarea
                value={draft.description}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
                rows={3}
                className="text-sm mt-1"
              />
            ) : (
              <div className="border rounded-md p-3 mt-1 text-sm bg-background">
                <MarkdownRenderer
                  content={workflow.description || '_(empty)_'}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Trigger</CardTitle>
          <CardDescription>
            How this workflow gets invoked. Edit the full trigger config in
            the Definition tab.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Badge variant="outline" className="text-xs capitalize">
            {triggerMode}
          </Badge>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Definition tab ───────────────────────────────────────────────────────
//
// JSON editor for v1. The shape inside `definition` is documented inline
// (steps, edges, layout). A visual graph editor is deferred.

function DefinitionTab({ workflow }: { workflow: CatalogWorkflowDetail }) {
  const update = useUpdateCatalogWorkflow();
  const [editing, setEditing] = useState(false);

  const [definitionDraft, setDefinitionDraft] = useState<string>(() =>
    JSON.stringify(workflow.definition ?? {}, null, 2),
  );
  const [triggerDraft, setTriggerDraft] = useState<string>(() =>
    JSON.stringify(workflow.triggerConfig ?? {}, null, 2),
  );
  const [inputDraft, setInputDraft] = useState<string>(() =>
    JSON.stringify(workflow.inputSchema ?? {}, null, 2),
  );
  const [outputDraft, setOutputDraft] = useState<string>(() =>
    JSON.stringify(workflow.outputSchema ?? {}, null, 2),
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateAndSave = async () => {
    const errs: Record<string, string> = {};
    let parsedDef: unknown = {};
    let parsedTrig: unknown = {};
    let parsedIn: unknown = {};
    let parsedOut: unknown = {};
    try {
      parsedDef = JSON.parse(definitionDraft || '{}');
    } catch (e) {
      errs.definition =
        e instanceof Error ? e.message : 'Invalid JSON';
    }
    try {
      parsedTrig = JSON.parse(triggerDraft || '{}');
    } catch (e) {
      errs.triggerConfig = e instanceof Error ? e.message : 'Invalid JSON';
    }
    try {
      parsedIn = JSON.parse(inputDraft || '{}');
    } catch (e) {
      errs.inputSchema = e instanceof Error ? e.message : 'Invalid JSON';
    }
    try {
      parsedOut = JSON.parse(outputDraft || '{}');
    } catch (e) {
      errs.outputSchema = e instanceof Error ? e.message : 'Invalid JSON';
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      await update.mutateAsync({
        id: workflow.id,
        definition: parsedDef as Record<string, unknown>,
        triggerConfig: parsedTrig as { mode: 'conversational' | 'scheduled' | 'event' } & Record<string, unknown>,
        inputSchema: parsedIn as Record<string, unknown>,
        outputSchema: parsedOut as Record<string, unknown>,
      });
      toast.success('Saved definition');
      setEditing(false);
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const isReadOnly = workflow.status === 'retired';

  const FieldEditor = ({
    label,
    value,
    onChange,
    error,
    placeholder,
    rows = 12,
  }: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    error?: string;
    placeholder?: string;
    rows?: number;
  }) => (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {editing ? (
        <>
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            placeholder={placeholder}
            className={cn(
              'text-xs font-mono',
              error && 'border-destructive',
            )}
          />
          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </>
      ) : (
        <pre className="border rounded-md p-3 bg-muted/30 text-xs font-mono overflow-x-auto max-h-[280px]">
          {value || '{}'}
        </pre>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base">Definition</CardTitle>
            <CardDescription>
              JSON shape of the workflow body. Steps, edges, layout. v1
              ships JSON editing; a visual graph editor is on the
              roadmap. Tenants clone this exact body into their workspace.
            </CardDescription>
          </div>
          {!isReadOnly &&
            (editing ? (
              <div className="flex gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditing(false);
                    setErrors({});
                    setDefinitionDraft(
                      JSON.stringify(workflow.definition ?? {}, null, 2),
                    );
                    setTriggerDraft(
                      JSON.stringify(workflow.triggerConfig ?? {}, null, 2),
                    );
                    setInputDraft(
                      JSON.stringify(workflow.inputSchema ?? {}, null, 2),
                    );
                    setOutputDraft(
                      JSON.stringify(workflow.outputSchema ?? {}, null, 2),
                    );
                  }}
                  title="Discard changes"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={validateAndSave}
                  disabled={update.isPending}
                  title="Save changes (creates a new version)"
                >
                  {update.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Save
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(true)}
                title="Edit JSON"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
            ))}
        </CardHeader>
        <CardContent className="space-y-4">
          <FieldEditor
            label="definition (steps, edges)"
            value={definitionDraft}
            onChange={setDefinitionDraft}
            error={errors.definition}
            placeholder='{"steps": []}'
            rows={16}
          />
          <FieldEditor
            label="triggerConfig"
            value={triggerDraft}
            onChange={setTriggerDraft}
            error={errors.triggerConfig}
            placeholder='{"mode": "conversational"}'
            rows={6}
          />
          <FieldEditor
            label="inputSchema (zod-compatible JSON Schema)"
            value={inputDraft}
            onChange={setInputDraft}
            error={errors.inputSchema}
            rows={8}
          />
          <FieldEditor
            label="outputSchema (zod-compatible JSON Schema)"
            value={outputDraft}
            onChange={setOutputDraft}
            error={errors.outputSchema}
            rows={8}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Composition tab ──────────────────────────────────────────────────────

function CompositionTab({ workflow }: { workflow: CatalogWorkflowDetail }) {
  const update = useUpdateCatalogWorkflow();
  const [pickerKind, setPickerKind] = useState<
    'skills' | 'tools' | 'functions' | null
  >(null);

  const handleRemoveSkill = async (skillId: string) => {
    try {
      await update.mutateAsync({
        id: workflow.id,
        referencedSkills: workflow.referencedSkills.filter(
          (id) => id !== skillId,
        ),
      });
      toast.success('Removed skill reference');
    } catch (err) {
      toast.error('Remove failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleRemoveTool = async (toolId: string) => {
    try {
      await update.mutateAsync({
        id: workflow.id,
        referencedTools: workflow.referencedTools.filter(
          (id) => id !== toolId,
        ),
      });
      toast.success('Removed tool reference');
    } catch (err) {
      toast.error('Remove failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleRemoveFunction = async (fnId: string) => {
    try {
      await update.mutateAsync({
        id: workflow.id,
        referencedFunctions: workflow.referencedFunctions.filter(
          (id) => id !== fnId,
        ),
      });
      toast.success('Removed function reference');
    } catch (err) {
      toast.error('Remove failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const isReadOnly = workflow.status === 'retired';
  const hasDangling =
    workflow.composition.danglingSkillIds.length > 0 ||
    workflow.composition.danglingToolIds.length > 0 ||
    workflow.composition.danglingFunctionIds.length > 0;

  return (
    <div className="space-y-4">
      {hasDangling && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="py-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
            <div>
              <div className="font-medium text-destructive">
                Dangling references detected
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                {workflow.composition.danglingSkillIds.length > 0 && (
                  <div>
                    {workflow.composition.danglingSkillIds.length} skill
                    reference(s) point to deleted rows.
                  </div>
                )}
                {workflow.composition.danglingToolIds.length > 0 && (
                  <div>
                    {workflow.composition.danglingToolIds.length} tool
                    reference(s) point to deleted rows.
                  </div>
                )}
                {workflow.composition.danglingFunctionIds.length > 0 && (
                  <div>
                    {workflow.composition.danglingFunctionIds.length}{' '}
                    function reference(s) point to deleted rows.
                  </div>
                )}
                <div className="pt-1">
                  Remove or replace before publishing.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Skills */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Skills ({workflow.composition.skills.length})
            </CardTitle>
            <CardDescription>
              System skills the workflow uses. On publish, every referenced
              skill must itself be in <span className="font-mono">published</span>{' '}
              status.
            </CardDescription>
          </div>
          {!isReadOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPickerKind('skills')}
              title="Add skill reference"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add skill
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {workflow.composition.skills.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No skill references yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Display name</TableHead>
                  <TableHead className="w-44">Slug</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  {!isReadOnly && <TableHead className="w-16" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {workflow.composition.skills.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-sm">{s.displayName}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {s.slug}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] capitalize',
                          statusPillClass(s.status),
                        )}
                      >
                        {s.status}
                      </Badge>
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveSkill(s.id)}
                          title="Remove reference"
                        >
                          <MinusCircle className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Tools */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Tools ({workflow.composition.tools.length})
            </CardTitle>
            <CardDescription>
              Direct system tool calls outside skill bundles.
            </CardDescription>
          </div>
          {!isReadOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPickerKind('tools')}
              title="Add tool reference"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add tool
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {workflow.composition.tools.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No tool references yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Display name</TableHead>
                  <TableHead className="w-44">Slug</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  {!isReadOnly && <TableHead className="w-16" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {workflow.composition.tools.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-sm">{t.displayName}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {t.slug}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] capitalize',
                          statusPillClass(t.status),
                        )}
                      >
                        {t.status}
                      </Badge>
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveTool(t.id)}
                          title="Remove reference"
                        >
                          <MinusCircle className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Functions */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FunctionSquare className="h-4 w-4" />
              Functions ({workflow.composition.functions.length})
            </CardTitle>
            <CardDescription>
              Argus wrapper functions invoked by the workflow.
            </CardDescription>
          </div>
          {!isReadOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPickerKind('functions')}
              title="Add function reference"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add function
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {workflow.composition.functions.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No function references yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Display name</TableHead>
                  <TableHead className="w-56">Function key</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  {!isReadOnly && <TableHead className="w-16" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {workflow.composition.functions.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="text-sm">{f.displayName}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {f.functionKey}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px]',
                          f.isActive
                            ? 'border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                            : 'border-amber-500/30 text-amber-700 dark:text-amber-400',
                        )}
                      >
                        {f.isActive ? 'active' : 'inactive'}
                      </Badge>
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveFunction(f.id)}
                          title="Remove reference"
                        >
                          <MinusCircle className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CompositionPicker
        kind={pickerKind}
        workflow={workflow}
        onClose={() => setPickerKind(null)}
      />
    </div>
  );
}

function CompositionPicker({
  kind,
  workflow,
  onClose,
}: {
  kind: 'skills' | 'tools' | 'functions' | null;
  workflow: CatalogWorkflowDetail;
  onClose: () => void;
}) {
  const skills = useSysAdminSystemSkills();
  const tools = useSysAdminSystemTools();
  const fns = useWrapperFunctions();
  const update = useUpdateCatalogWorkflow();
  const [search, setSearch] = useState('');

  const items = useMemo(() => {
    if (kind === 'skills') {
      const all = skills.data?.items ?? [];
      const existing = new Set(workflow.referencedSkills);
      const candidates = all.filter((s: any) => !existing.has(s.id));
      if (!search) return candidates;
      const q = search.toLowerCase();
      return candidates.filter(
        (s: any) =>
          s.slug.toLowerCase().includes(q) ||
          s.displayName.toLowerCase().includes(q),
      );
    }
    if (kind === 'tools') {
      const all = tools.data?.items ?? [];
      const existing = new Set(workflow.referencedTools);
      const candidates = all.filter((t: any) => !existing.has(t.id));
      if (!search) return candidates;
      const q = search.toLowerCase();
      return candidates.filter(
        (t: any) =>
          t.slug.toLowerCase().includes(q) ||
          t.displayName.toLowerCase().includes(q),
      );
    }
    if (kind === 'functions') {
      const all = fns.data?.items ?? [];
      const existing = new Set(workflow.referencedFunctions);
      const candidates = all.filter((f: any) => !existing.has(f.id));
      if (!search) return candidates;
      const q = search.toLowerCase();
      return candidates.filter(
        (f: any) =>
          f.functionKey.toLowerCase().includes(q) ||
          f.displayName.toLowerCase().includes(q),
      );
    }
    return [];
  }, [kind, skills.data, tools.data, fns.data, workflow, search]);

  const handleAdd = async (id: string) => {
    try {
      if (kind === 'skills') {
        await update.mutateAsync({
          id: workflow.id,
          referencedSkills: [...workflow.referencedSkills, id],
        });
        toast.success('Added skill reference');
      } else if (kind === 'tools') {
        await update.mutateAsync({
          id: workflow.id,
          referencedTools: [...workflow.referencedTools, id],
        });
        toast.success('Added tool reference');
      } else if (kind === 'functions') {
        await update.mutateAsync({
          id: workflow.id,
          referencedFunctions: [...workflow.referencedFunctions, id],
        });
        toast.success('Added function reference');
      }
    } catch (err) {
      toast.error('Add failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const isLoading =
    kind === 'skills'
      ? skills.isLoading
      : kind === 'tools'
        ? tools.isLoading
        : fns.isLoading;
  const error =
    kind === 'skills'
      ? skills.error
      : kind === 'tools'
        ? tools.error
        : fns.error;

  const labelKind =
    kind === 'skills'
      ? 'skill'
      : kind === 'tools'
        ? 'tool'
        : 'function';

  return (
    <Sheet open={kind !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Add {labelKind} reference</SheetTitle>
          <SheetDescription>
            References are added one at a time so the workflow row's
            version increments cleanly.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-3 mt-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder={`Search ${kind ?? ''}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>

          {isLoading ? (
            <div className="py-10 text-center text-muted-foreground">
              <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
              Loading…
            </div>
          ) : error ? (
            <div className="py-6 text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              {error.message}
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {search
                ? `No matches for "${search}".`
                : `Nothing left to add — every available ${labelKind} is already referenced.`}
            </div>
          ) : (
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {items.map((it: any) => {
                const id = it.id as string;
                const slug = (it.slug ?? it.functionKey) as string;
                const name = it.displayName as string;
                const status = it.status as string | undefined;
                const active = (it.isActive ?? true) as boolean;
                return (
                  <div
                    key={id}
                    className="flex items-center justify-between border rounded p-2 hover:bg-muted/40"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{name}</div>
                      <div className="text-[11px] text-muted-foreground font-mono truncate">
                        {slug}
                      </div>
                    </div>
                    {status && status !== 'published' && (
                      <Badge
                        variant="outline"
                        className="text-[10px] mr-2 capitalize"
                      >
                        {status}
                      </Badge>
                    )}
                    {kind === 'functions' && !active && (
                      <Badge variant="outline" className="text-[10px] mr-2">
                        inactive
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleAdd(id)}
                      disabled={update.isPending}
                      title="Add reference"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Versions tab ─────────────────────────────────────────────────────────

function VersionsTab({ workflow }: { workflow: CatalogWorkflowDetail }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Version history</CardTitle>
        <CardDescription>
          Every PATCH snapshots the prior row to{' '}
          <code className="font-mono text-[10px] bg-muted px-1 rounded">
            system_workflow_versions
          </code>
          . Current row is v{workflow.version}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {workflow.versions.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No prior versions yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Version</TableHead>
                <TableHead>Changed at</TableHead>
                <TableHead>Changed by (admin id)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workflow.versions.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="tabular-nums">v{v.version}</TableCell>
                  <TableCell className="text-xs tabular-nums">
                    {new Date(v.changedAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-mono text-[11px]">
                    {v.changedByAdmin ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Clones tab ───────────────────────────────────────────────────────────

function ClonesTab({ workflow }: { workflow: CatalogWorkflowDetail }) {
  const clones = useCatalogWorkflowClones(workflow.id);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Tenant clones</CardTitle>
        <CardDescription>
          Cross-tenant view of every clone derived from this template.
          CLONE inheritance — tenant copies are independent post-fork; the
          sysadmin cannot revoke a clone, only stop new clones via
          unpublish. <span className="font-mono">sync_status</span> tracks
          whether the tenant copy is still aligned with the source version
          they cloned.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {clones.isLoading ? (
          <div className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
            Loading…
          </div>
        ) : clones.error ? (
          <div className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            {clones.error.message}
          </div>
        ) : (clones.data?.items ?? []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No tenant clones yet.{' '}
            {workflow.status !== 'published' && (
              <>
                Publish the template so tenants can clone it.
              </>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead className="w-32">Source version</TableHead>
                <TableHead className="w-32">Sync status</TableHead>
                <TableHead className="w-44">Cloned at</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(clones.data?.items ?? []).map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="text-sm">
                    {c.tenantName ?? c.tenantId}
                  </TableCell>
                  <TableCell className="text-xs tabular-nums">
                    v{c.sourceVersion}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px] capitalize',
                        syncStatusPillClass(c.syncStatus),
                      )}
                    >
                      {c.syncStatus.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {new Date(c.clonedAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.notes ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Audit tab ────────────────────────────────────────────────────────────

function AuditTab({ workflow }: { workflow: CatalogWorkflowDetail }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Audit</CardTitle>
        <CardDescription>
          Every mutation on this workflow (create / update / publish /
          unpublish / delete) is hash-chained into the platform audit log.
          Tenant clone events are recorded against the cloning tenant's
          audit chain.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Published at</div>
            <div className="tabular-nums mt-0.5">
              {workflow.publishedAt
                ? new Date(workflow.publishedAt).toLocaleString()
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Published by</div>
            <div className="font-mono text-xs mt-0.5">
              {workflow.publishedBy ?? '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Unpublished at</div>
            <div className="tabular-nums mt-0.5">
              {workflow.unpublishedAt
                ? new Date(workflow.unpublishedAt).toLocaleString()
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Unpublished by</div>
            <div className="font-mono text-xs mt-0.5">
              {workflow.unpublishedBy ?? '—'}
            </div>
          </div>
        </div>
        {workflow.unpublishReason && (
          <div className="border rounded-md p-3 bg-muted/30">
            <div className="text-xs text-muted-foreground mb-1">
              Unpublish reason
            </div>
            <div className="text-sm">{workflow.unpublishReason}</div>
          </div>
        )}
        <div className="pt-2">
          <Link
            to="/sysadmin/audit"
            search={{ resourceId: workflow.id }}
            className="inline-flex items-center text-sm text-primary hover:underline"
          >
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            View full audit history in sysadmin Audit module
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
